import { DemoFixture, EXPECTED_CIRCUIT_ID, ProofFixture, Snapshot } from './demoData';
import { canonicalStringify, hashProof, parseUsername, sha256Hex } from './verification';

const BN254_FR_MODULUS = BigInt(
  '0x30644e72e131a029b85045b68181585d2833e84879b9709143e1f593f0000001',
);

export type CsvProofOptions = {
  assets: string;
  attestor: string;
  fileName: string;
  period: string;
  proofIndex: number;
  subject: string;
};

type LiabilityEntry = {
  username: string;
  usernameToBigInt: string;
  balance: bigint;
};

type TreeNode = {
  hash: string;
  sum: bigint;
};

type GeneratedTree = {
  depth: number;
  entries: LiabilityEntry[];
  leafCount: number;
  levels: TreeNode[][];
  root: TreeNode;
};

export async function buildFixtureFromCsv(
  csvText: string,
  options: CsvProofOptions,
): Promise<DemoFixture> {
  const entries = parseLiabilityCsv(csvText);

  if (
    !Number.isInteger(options.proofIndex) ||
    options.proofIndex < 0 ||
    options.proofIndex >= entries.length
  ) {
    throw new Error('Proof row must refer to a row in the CSV');
  }

  const tree = await buildTree(entries);
  const assets = options.assets.trim()
    ? parseNonNegativeAmount(options.assets, 'assets')
    : tree.root.sum;
  const proof = buildProof(tree, options.proofIndex);
  const report = {
    assets: assets.toString(10),
    attestor: options.attestor.trim() || 'local-attestor',
    csv: options.fileName || 'local-liabilities.csv',
    ledger: 0,
    liabilities: tree.root.sum.toString(10),
    period: options.period.trim() || new Date().toISOString().slice(0, 7),
    proofIndex: options.proofIndex,
    rows: entries.length,
    subject: options.subject.trim() || 'local-issuer',
  };
  const reportHash = await sha256Hex(canonicalStringify(report));
  const snapshot: Snapshot = {
    root_hash: tree.root.hash,
    liabilities: tree.root.sum.toString(10),
    assets: assets.toString(10),
    proof_hash: '0'.repeat(64),
    circuit_id: EXPECTED_CIRCUIT_ID,
    report_hash: reportHash,
  };
  snapshot.proof_hash = await hashProof(snapshot, proof);

  return {
    id: `csv-${Date.now()}`,
    label: options.fileName ? `Generated from ${options.fileName}` : 'Generated CSV proof',
    description: `Local proof package generated from ${entries.length} liability rows.`,
    expectedPass: assets >= tree.root.sum,
    report,
    snapshot,
    proof,
    diagnostics: {
      assetCoverage: formatRatio(assets, tree.root.sum),
      csvRows: entries.length,
      leafCount: tree.leafCount,
      proofUsername: proof.username,
      treeDepth: tree.depth,
    },
  };
}

export function parseLiabilityCsv(csvText: string): LiabilityEntry[] {
  const rows = parseCsvRows(csvText).filter((row) => row.some((cell) => cell.trim()));
  if (rows.length < 2) {
    throw new Error('CSV must include a header row and at least one liability row');
  }

  const header = rows[0].map((cell) => cell.trim().toLowerCase());
  const usernameIndex = header.indexOf('username');
  const balanceIndex = header.indexOf('balance');

  if (usernameIndex === -1 || balanceIndex === -1) {
    throw new Error('CSV header must include username and balance columns');
  }

  return rows.slice(1).map((row, rowIndex) => {
    const username = row[usernameIndex]?.trim();
    const balanceText = row[balanceIndex]?.trim();
    if (!username) {
      throw new Error(`Row ${rowIndex + 2} is missing a username`);
    }
    const balance = parseNonNegativeAmount(balanceText, `row ${rowIndex + 2} balance`);

    return {
      username,
      usernameToBigInt: parseUsername(username).toString(10),
      balance,
    };
  });
}

async function buildTree(entries: LiabilityEntry[]): Promise<GeneratedTree> {
  const leafCount = nextPowerOfTwo(entries.length);
  const paddedEntries = [...entries];
  while (paddedEntries.length < leafCount) {
    paddedEntries.push({ username: '', usernameToBigInt: '0', balance: 0n });
  }

  const levels: TreeNode[][] = [
    await Promise.all(
      paddedEntries.map((entry) => hashNode('leaf', entry.usernameToBigInt, entry.balance)),
    ),
  ];

  while (levels[levels.length - 1].length > 1) {
    const previous = levels[levels.length - 1];
    const nextLevel: TreeNode[] = [];
    for (let index = 0; index < previous.length; index += 2) {
      nextLevel.push(await parentNode(previous[index], previous[index + 1]));
    }
    levels.push(nextLevel);
  }

  return {
    depth: Math.max(0, levels.length - 1),
    entries,
    leafCount,
    levels,
    root: levels[levels.length - 1][0],
  };
}

function buildProof(tree: GeneratedTree, proofIndex: number): ProofFixture {
  const entry = tree.entries[proofIndex];
  let index = proofIndex;
  const pathIndices: number[] = [];
  const siblingsHashes: string[] = [];
  const siblingsSums: string[] = [];

  for (let level = 0; level < tree.depth; level += 1) {
    const position = index % 2;
    const siblingIndex = position === 0 ? index + 1 : index - 1;
    const sibling = tree.levels[level][siblingIndex];
    pathIndices.push(position);
    siblingsHashes.push(BigInt(`0x${sibling.hash}`).toString(10));
    siblingsSums.push(sibling.sum.toString(10));
    index = Math.floor(index / 2);
  }

  return {
    username: entry.username,
    usernameToBigInt: entry.usernameToBigInt,
    balance: entry.balance.toString(10),
    pathIndices,
    siblingsHashes,
    siblingsSums,
  };
}

async function parentNode(left: TreeNode, right: TreeNode): Promise<TreeNode> {
  return {
    hash: await fieldHashHex(
      canonicalStringify({
        leftHash: left.hash,
        leftSum: left.sum,
        rightHash: right.hash,
        rightSum: right.sum,
        type: 'node',
      }),
    ),
    sum: left.sum + right.sum,
  };
}

async function hashNode(type: 'leaf', usernameToBigInt: string, balance: bigint): Promise<TreeNode> {
  return {
    hash: await fieldHashHex(canonicalStringify({ balance, type, usernameToBigInt })),
    sum: balance,
  };
}

async function fieldHashHex(value: string): Promise<string> {
  const digest = await sha256Hex(value);
  return (BigInt(`0x${digest}`) % BN254_FR_MODULUS).toString(16).padStart(64, '0');
}

function parseCsvRows(csvText: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index];
    const next = csvText[index + 1];

    if (char === '"' && quoted && next === '"') {
      cell += '"';
      index += 1;
      continue;
    }
    if (char === '"') {
      quoted = !quoted;
      continue;
    }
    if (char === ',' && !quoted) {
      row.push(cell);
      cell = '';
      continue;
    }
    if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') {
        index += 1;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell);
  rows.push(row);
  return rows;
}

function parseNonNegativeAmount(value: string | undefined, label: string): bigint {
  if (!value || !/^\d+$/.test(value.trim())) {
    throw new Error(`${label} must be a non-negative integer`);
  }
  return BigInt(value.trim());
}

function nextPowerOfTwo(value: number): number {
  let output = 1;
  while (output < value) {
    output *= 2;
  }
  return output;
}

function formatRatio(assets: bigint, liabilities: bigint): string {
  if (liabilities === 0n) {
    return assets === 0n ? '1.00x' : 'open';
  }
  return (Number(assets) / Number(liabilities)).toFixed(2) + 'x';
}
